import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileText, Loader2, Paperclip, UserCheck, UserPlus } from 'lucide-react';
import { api, type FacturaHoldedPreview } from '@/lib/api';
import { fmt } from '@/lib/liquidaciones';
import { classNames } from '@/lib/utils';

/**
 * Paso 5 del asistente: la factura de Sereno al propietario en Holded.
 *
 * Enseña la factura que se va a crear —cliente, líneas y totales— y la
 * compara con lo que la liquidación descuenta, que es lo que debe sumar. Al
 * crearla le adjunta la liquidación en PDF, para que en Holded estén las dos.
 * Una vez creada, el PDF de Holded se ve aquí mismo.
 */
export default function PasoFacturaHolded({
  liquidacion,
  generarPdfLiquidacion,
  onLiquidacionActualizada,
  onAtras,
  onContinuar,
}: {
  liquidacion: any;
  /** El mismo PDF que «Descargar», en base64 */
  generarPdfLiquidacion: () => Promise<{ nombre: string; base64: string }>;
  onLiquidacionActualizada: (liq: any) => void;
  onAtras?: () => void;
  onContinuar: () => void;
}) {
  const [preview, setPreview] = useState<FacturaHoldedPreview | null>(null);
  const [cargando, setCargando] = useState(true);
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState('');
  const [confirmarAprobar, setConfirmarAprobar] = useState(false);
  const [aprobando, setAprobando] = useState(false);
  const [adjuntando, setAdjuntando] = useState(false);

  // La del servidor puede venir más al día (aprobada desde Holded) que la que
  // tiene el asistente en memoria, y al revés justo después de crearla
  const candidatas = [liquidacion.holdedInvoice, preview?.creada].filter(Boolean);
  const creada = candidatas.find((c: any) => !c.draft) || candidatas[0] || null;
  const adjunto = liquidacion.holdedInvoice?.adjunto || preview?.creada?.adjunto || null;

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setError('');
    api.getFacturaHolded(liquidacion.id)
      .then(p => {
        if (!vivo) return;
        setPreview(p);
        // Aprobada desde Holded mientras tanto: que el paso 6 lo sepa
        if (p.creada && !p.creada.draft && liquidacion.holdedInvoice?.draft) {
          onLiquidacionActualizada({ ...liquidacion, holdedInvoice: p.creada });
        }
      })
      .catch(err => { if (vivo) setError(err?.message || 'No se pudo preparar la factura'); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [liquidacion.id]);

  async function crear() {
    setCreando(true);
    setError('');
    try {
      const r = await api.crearFacturaHolded(liquidacion.id);
      onLiquidacionActualizada(r.liquidacion);
      await adjuntar();
    } catch (err: any) {
      setError(err?.message || 'No se pudo crear la factura en Holded');
    } finally {
      setCreando(false);
    }
  }

  /** La liquidación en PDF, adjunta a la factura. Si falla, la factura queda creada y se puede repetir. */
  async function adjuntar() {
    setAdjuntando(true);
    try {
      const r = await api.adjuntarLiquidacionHolded(liquidacion.id, await generarPdfLiquidacion());
      onLiquidacionActualizada(r.liquidacion);
    } catch (err: any) {
      setError(`La factura está creada, pero no se pudo adjuntar la liquidación: ${err?.message || 'error'}. Prueba otra vez con «Adjuntar liquidación».`);
    } finally {
      setAdjuntando(false);
    }
  }

  async function aprobar() {
    setAprobando(true);
    setError('');
    try {
      const r = await api.aprobarFacturaHolded(liquidacion.id);
      onLiquidacionActualizada(r.liquidacion);
      setConfirmarAprobar(false);
      onContinuar();
    } catch (err: any) {
      setError(err?.message || 'No se pudo aprobar la factura en Holded');
      setConfirmarAprobar(false);
    } finally {
      setAprobando(false);
    }
  }

  const botonesPie = (principal: React.ReactNode) => (
    <div className="flex justify-between items-center pt-4">
      {onAtras ? <button onClick={onAtras} className="btn-ghost flex items-center gap-2">← Atrás</button> : <span />}
      {principal}
    </div>
  );

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-sereno-dark">Factura en Holded</h2>
        {preview?.aplica && preview.cuenta === 'test' && (
          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-purple-100 text-purple-700 border border-purple-100">
            Cuenta de pruebas · DEMO SERENO TECH
          </span>
        )}
      </div>

      {cargando ? (
        <div className="flex items-center gap-2 text-sm text-sereno-dark/60 py-8 justify-center">
          <Loader2 className="animate-spin" size={16} /> Preparando la factura…
        </div>
      ) : creada ? (
        <FacturaCreada liquidacionId={liquidacion.id} factura={creada} adjunto={adjunto} adjuntando={adjuntando} onAdjuntar={() => { setError(''); adjuntar(); }} error={error} pie={botonesPie(
          creada.draft ? (
            <div className="flex items-center gap-3">
              <button onClick={onContinuar} disabled={aprobando} className="btn-ghost flex items-center gap-2">
                Conservar borrador
              </button>
              <button
                onClick={() => setConfirmarAprobar(true)}
                disabled={aprobando}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors disabled:opacity-50"
              >
                {aprobando ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                Aprobar
              </button>
            </div>
          ) : (
            <button onClick={onContinuar} className="btn-primary flex items-center gap-2">Continuar →</button>
          )
        )} />
      ) : !preview ? (
        <>
          <Aviso tono="error">{error || 'No se pudo preparar la factura.'}</Aviso>
          {botonesPie(<button onClick={onContinuar} className="btn-ghost">Saltar este paso →</button>)}
        </>
      ) : !preview.aplica ? (
        <>
          <Aviso tono="info">{preview.motivo}</Aviso>
          {botonesPie(<button onClick={onContinuar} className="btn-primary flex items-center gap-2">Continuar →</button>)}
        </>
      ) : (
        <Propuesta
          preview={preview}
          error={error}
          pie={botonesPie(
            <div className="flex items-center gap-3">
              <button onClick={onContinuar} className="btn-ghost text-sm">Saltar este paso</button>
              <button
                onClick={crear}
                disabled={creando || !preview.contacto.documento || preview.lineas.length === 0 || preview.lineas.some(l => !l.servicioEnHolded) || Boolean(preview.serieFalta)}
                className="btn-primary flex items-center gap-2"
              >
                {creando ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />}
                Crear en Holded
              </button>
            </div>
          )}
        />
      )}

      {confirmarAprobar && creada && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full space-y-4">
            <h3 className="text-lg font-bold text-sereno-dark">Aprobar la factura</h3>
            <p className="text-sm text-sereno-dark/70">
              Holded le pondrá número de la serie y quedará emitida{creada.cuenta === 'prod' ? ', y se enviará a la Agencia Tributaria por Verifactu' : ''}.
              {' '}<strong>No se puede deshacer</strong>: después solo se corrige con una factura rectificativa.
            </p>
            {creada.total != null && <p className="text-sm text-sereno-dark">Total: <strong>{fmt(creada.total)}</strong></p>}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setConfirmarAprobar(false)} disabled={aprobando} className="btn-ghost flex-1">Cancelar</button>
              <button
                onClick={aprobar}
                disabled={aprobando}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-50"
              >
                {aprobando ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                {aprobando ? 'Aprobando…' : 'Aprobar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Propuesta({ preview, error, pie }: {
  preview: Extract<FacturaHoldedPreview, { aplica: true }>;
  error: string;
  pie: React.ReactNode;
}) {
  const { contacto } = preview;
  const faltanServicios = preview.lineas.filter(l => !l.servicioEnHolded).map(l => l.codigo);
  const dif = preview.diferencia;
  const fecha = new Date(`${preview.fecha}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 p-4 space-y-1 text-sm">
          <p className="text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1">Cliente</p>
          <p className="font-semibold text-sereno-dark">{contacto.nombre}</p>
          {contacto.direccion && <p className="text-sereno-dark/70">{contacto.direccion}</p>}
          <p className="text-sereno-dark/70">{contacto.documento || <span className="text-red-600">Sin DNI/CIF</span>}</p>
          <p className={classNames('flex items-center gap-1.5 text-xs pt-1', contacto.enHolded ? 'text-green-700' : 'text-amber-700')}>
            {contacto.enHolded ? <UserCheck size={14} /> : <UserPlus size={14} />}
            {contacto.enHolded
              ? `Ya está en Holded${contacto.nombreEnHolded && contacto.nombreEnHolded !== contacto.nombre ? ` como «${contacto.nombreEnHolded}»` : ''}`
              : 'No está en Holded: se dará de alta con este DNI'}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 p-4 space-y-1 text-sm">
          <p className="text-xs font-semibold text-sereno-dark/60 uppercase tracking-wide mb-1">Factura</p>
          <p><span className="text-sereno-dark/60">Fecha:</span> <span className="text-sereno-dark">{fecha}</span></p>
          <p><span className="text-sereno-dark/60">Serie:</span>{' '}
            {preview.serieFalta
              ? <span className="text-red-600">falta la serie «{preview.serieFalta}»</span>
              : <span className="text-sereno-dark">{preview.serie || 'la predeterminada de Holded'}</span>}
          </p>
          <p className="text-xs text-sereno-dark/50 pt-1">El número lo pone Holded al crearla.</p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-sereno-dark/60">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Concepto</th>
              <th className="text-right px-4 py-2.5 font-semibold">Subtotal</th>
              <th className="text-right px-4 py-2.5 font-semibold">IVA</th>
              <th className="text-right px-4 py-2.5 font-semibold">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {preview.lineas.map(l => (
              <tr key={l.codigo}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-sereno-dark">{l.nombre}</p>
                  <p className="text-sereno-dark/70">{l.descripcion}</p>
                  <p className="text-xs text-sereno-dark/40 mt-0.5">De la liquidación: {l.origen}</p>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{fmt(l.base)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-sereno-dark/70">21%</td>
                <td className="px-4 py-3 text-right tabular-nums font-medium">{fmt(l.total)}</td>
              </tr>
            ))}
            {preview.lineas.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-sereno-dark/50">La liquidación no tiene nada que facturar.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <Cuadre total={preview.total} cargos={preview.cargosLiquidacion} dif={dif} />
        <dl className="text-sm min-w-[16rem] space-y-1.5">
          <div className="flex justify-between"><dt className="font-semibold">Base imponible</dt><dd className="tabular-nums">{fmt(preview.subtotal)}</dd></div>
          <div className="flex justify-between"><dt className="font-semibold">IVA 21%</dt><dd className="tabular-nums">{fmt(preview.iva)}</dd></div>
          <div className="flex justify-between border-t-2 border-sereno-dark/20 pt-2 text-base font-bold text-sereno-dark">
            <dt>Total</dt><dd className="tabular-nums">{fmt(preview.total)}</dd>
          </div>
        </dl>
      </div>

      {!contacto.documento && (
        <Aviso tono="error">El propietario no tiene DNI/CIF en su ficha: sin él no se puede buscar ni dar de alta en Holded.</Aviso>
      )}
      {preview.serieFalta && (
        <Aviso tono="error">
          Esta factura es de {preview.serieFalta} y en Holded no existe todavía la serie «{preview.serieFalta}». Créala en Holded
          (Configuración &gt; Numeración, igual que la de {Number(preview.serieFalta) - 1}) y vuelve a abrir este paso.
        </Aviso>
      )}
      {faltanServicios.length > 0 && (
        <Aviso tono="error">
          Faltan servicios en Holded con código {faltanServicios.map(c => `«${c}»`).join(', ')} (Ventas &gt; Servicios).
        </Aviso>
      )}
      {error && <Aviso tono="error">{error}</Aviso>}
      {pie}
    </>
  );
}

function Cuadre({ total, cargos, dif }: { total: number; cargos: number | null; dif: number | null }) {
  if (cargos == null || dif == null) return <span />;
  const cuadra = Math.abs(dif) < 0.005;
  const centimos = !cuadra && Math.abs(dif) <= 0.05;
  return (
    <div className={classNames(
      'flex items-start gap-2 text-sm rounded-lg border p-3 sm:max-w-sm',
      cuadra ? 'bg-green-50 border-green-200 text-green-800' : centimos ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-red-50 border-red-200 text-red-700',
    )}>
      {cuadra ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
      <span>
        {cuadra
          ? <>Cuadra con lo que se descuenta en la liquidación ({fmt(cargos)}).</>
          : <>La liquidación descuenta {fmt(cargos)} y la factura suma {fmt(total)}: {fmt(Math.abs(dif))} de diferencia{centimos ? ', por el redondeo de cada línea' : ''}.</>}
      </span>
    </div>
  );
}

function FacturaCreada({ liquidacionId, factura, adjunto, adjuntando, onAdjuntar, error, pie }: {
  liquidacionId: string;
  factura: any;
  adjunto: { nombre: string } | null;
  adjuntando: boolean;
  onAdjuntar: () => void;
  error?: string;
  pie: React.ReactNode;
}) {
  const [cargandoPdf, setCargandoPdf] = useState(true);
  const url = api.urlPdfFacturaHolded(liquidacionId);
  return (
    <>
      <div className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
        <CheckCircle2 className="text-green-600 shrink-0" size={22} />
        <div className="text-sm">
          <p className="font-semibold text-green-800">
            {factura.draft ? 'Borrador de factura creado en Holded' : `Factura ${factura.docNumber || ''} aprobada en Holded`}
            {factura.cuenta === 'test' ? ' (cuenta de pruebas)' : ''}
          </p>
          <p className="text-green-700/70">
            {factura.total != null ? `${fmt(factura.total)} · ` : ''}
            {factura.draft
              ? 'Apruébala para emitirla con número, o consérvala en borrador y envía el borrador al propietario.'
              : 'Emitida con número: es la que se envía en el paso siguiente.'}
          </p>
        </div>
        <a href={url} target="_blank" rel="noreferrer" className="ml-auto text-sm text-sereno-dark underline whitespace-nowrap">Abrir PDF</a>
      </div>
      <div className={classNames(
        'flex items-center gap-3 rounded-lg border p-3 text-sm',
        adjunto ? 'border-gray-200 text-sereno-dark/70' : 'border-amber-200 bg-amber-50 text-amber-800',
      )}>
        {adjuntando ? <Loader2 className="animate-spin shrink-0" size={16} /> : <Paperclip className="shrink-0" size={16} />}
        <span className="flex-1">
          {adjuntando
            ? 'Adjuntando la liquidación a la factura…'
            : adjunto
              ? <>Liquidación adjunta en Holded <span className="font-mono text-xs">({adjunto.nombre})</span></>
              : 'La factura todavía no lleva la liquidación adjunta en Holded.'}
        </span>
        {!adjunto && !adjuntando && (
          <button onClick={onAdjuntar} className="btn-ghost text-sm whitespace-nowrap">Adjuntar liquidación</button>
        )}
      </div>
      <div className="relative rounded-xl border border-gray-200 overflow-hidden bg-gray-50" style={{ height: '70vh' }}>
        {cargandoPdf && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-sereno-dark/60">
            <Loader2 className="animate-spin" size={16} /> Cargando el PDF de Holded…
          </div>
        )}
        {/* La key recarga el PDF cuando la factura pasa de borrador a aprobada */}
        <iframe key={`${factura.id}-${factura.draft ? 'b' : 'a'}`} title="Factura de Holded" src={url} className="w-full h-full" onLoad={() => setCargandoPdf(false)} />
      </div>
      {error && <Aviso tono="error">{error}</Aviso>}
      {pie}
    </>
  );
}

function Aviso({ tono, children }: { tono: 'info' | 'error'; children: React.ReactNode }) {
  return (
    <div className={classNames(
      'flex items-start gap-2 text-sm rounded-lg border p-3',
      tono === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800',
    )}>
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}
